import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import SignatureFieldsSidebar from "../../../pages/contract-dashboard/components/signature-field-editor/SignatureFieldsSidebar";
import FakeAppChrome from "../FakeAppChrome";
import TourPdfStage from "../TourPdfStage";
import { TOUR_DOCUMENT, TOUR_SIGNATURE_FIELDS } from "../tourData";
import { useTourSamplePdf } from "../useTourSamplePdf";

/**
 * Mirrors SignatureFieldEditorModal layout + animates fields being placed
 * one by one (same SignatureBox overlays the real editor uses).
 */
const SignScene = () => {
  const { pdfBlob, loadingPdf, loadError } = useTourSamplePdf();
  const [placedCount, setPlacedCount] = useState(0);
  const [phase, setPhase] = useState("placing"); // placing | ready | sent

  const visibleFields = useMemo(
    () => TOUR_SIGNATURE_FIELDS.slice(0, placedCount),
    [placedCount]
  );

  const selectedSignerId =
    visibleFields[visibleFields.length - 1]?.signer_id ?? null;

  useEffect(() => {
    if (loadingPdf || loadError || !pdfBlob) return undefined;

    const timers = [];
    const total = TOUR_SIGNATURE_FIELDS.length;
    // Place each signature field with clear pauses
    for (let i = 1; i <= total; i += 1) {
      timers.push(
        window.setTimeout(() => {
          setPlacedCount(i);
        }, 1200 + (i - 1) * 2000)
      );
    }
    timers.push(
      window.setTimeout(() => {
        setPhase("ready");
      }, 1200 + total * 2000 + 800)
    );
    timers.push(
      window.setTimeout(() => {
        setPhase("sent");
      }, 1200 + total * 2000 + 2400)
    );

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [loadingPdf, loadError, pdfBlob]);

  return (
    <FakeAppChrome activeNavId="generator">
      <div className="relative flex min-h-full flex-col p-2 sm:p-3">
        {/* Dimmed app backdrop — signature flow is a modal in production */}
        <div className="pointer-events-none absolute inset-0 bg-black/40" aria-hidden />

        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-[1] flex min-h-[min(78dvh,52rem)] flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-elevation-lg"
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-3 py-2 lg:px-4 lg:py-4 md:px-5">
            <h1 className="truncate pr-2 text-sm font-semibold lg:text-lg">
              Campos de assinatura — {TOUR_DOCUMENT.title}
            </h1>
            <span className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground">
              <Icon name="X" size={18} />
            </span>
          </div>

          <div className="hidden flex-shrink-0 items-center justify-end gap-2 overflow-x-auto border-b border-border px-4 py-2 md:px-5 lg:flex">
            <Button type="button" variant="outline" size="sm" tabIndex={-1}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              iconName="Save"
              tabIndex={-1}
              disabled={placedCount < TOUR_SIGNATURE_FIELDS.length}
            >
              Salvar campos
            </Button>
            <Button
              type="button"
              size="sm"
              iconName="Send"
              tabIndex={-1}
              disabled={phase !== "sent" && phase !== "ready"}
              className={
                phase === "sent"
                  ? "bg-success text-success-foreground hover:bg-success"
                  : undefined
              }
            >
              {phase === "sent" ? "Enviado" : "Enviar para assinatura"}
            </Button>
          </div>

          <AnimatePresence>
            {phase === "placing" && placedCount > 0 ? (
              <motion.div
                key="placing-banner"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="shrink-0 border-b border-primary/20 bg-primary/10 px-4 py-2 text-xs sm:text-sm text-foreground"
              >
                Posicionando campo de assinatura
                {visibleFields[placedCount - 1]
                  ? ` — ${visibleFields[placedCount - 1].full_name}…`
                  : "…"}
              </motion.div>
            ) : null}
            {phase === "sent" ? (
              <motion.div
                key="sent-banner"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="shrink-0 border-b border-success/30 bg-success/10 px-4 py-2 text-xs sm:text-sm text-foreground flex items-center gap-2"
              >
                <Icon name="Send" size={14} className="text-success shrink-0" />
                Documento enviado para assinatura — Roberto, Ana e Carlos
                receberiam o link.
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
            <div className="relative min-h-[22rem] flex-1 min-w-0 border-b border-border lg:border-b-0 lg:border-r">
              <TourPdfStage
                pdfBlob={pdfBlob}
                loadingPdf={loadingPdf}
                loadError={loadError}
                fields={visibleFields}
                selectedSignerId={selectedSignerId}
                framed={false}
                className="absolute inset-0 h-full min-h-0"
                currentPage={3}
                totalPages={4}
              />
              {/* Pulse ring when a new box is placed */}
              <AnimatePresence>
                {phase === "placing" && placedCount > 0 ? (
                  <motion.div
                    key={`pulse-${placedCount}`}
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-primary/40"
                  />
                ) : null}
              </AnimatePresence>
            </div>

            <div className="hidden min-h-0 w-full max-h-[20rem] flex-shrink-0 flex-col overflow-hidden lg:flex lg:max-h-none lg:w-[320px] xl:w-[360px]">
              <div className="min-h-0 flex-1 overflow-hidden">
                <SignatureFieldsSidebar
                  fields={visibleFields}
                  selectedSignerId={selectedSignerId}
                  onSelect={() => {}}
                  onEdit={() => {}}
                  onRemove={() => {}}
                  disabled
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </FakeAppChrome>
  );
};

export default SignScene;
